import React, { useState, useMemo, useEffect, Fragment } from 'react';
import logo from './MT_logo.svg';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { projectEntrySelectors, projectEntryActions, bulkEntryActions } from '@/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropdownItem, DropdownMenu, DropdownToggle, Collapse } from 'reactstrap';
import { useToggle } from '@/hooks';
import BulkTimeEntry from '../../../../components/BulkTimeEntry/BulkTimeEntry';
import { Routes, SupportedSystems } from '../../../../shared';
import { isArrayWithLength, formatClassNames } from '../../../../helpers';
import { powerBiReportsUrl } from '../../../../configs';
import { 
  List,
  Nav,
  NavLink,
  NavItem,
  Overlay,
  Toggle,
  StyledNavbar, 
  StyledLogo, 
  StyledDropdown, 
  SystemDrawerWrapper, 
  BulkEntryIconLabel, 
  BulkEntryIconWrapper, 
  StyledUserDropdown,
  PlainAnchor,
} from './styled';
import { useViewportSize } from '@mantine/hooks';
import { mantineTheme as theme } from '../../../../theme';

const Hamburger = ({ active, onClick }) => (
  <Toggle 
    type='button' 
    onClick={onClick}
    className={formatClassNames(['hamburger', 'hamburger--spring', active && 'is-active'])} 
  >
    <span className='hamburger-box'>
      <span className='hamburger-inner' />
    </span>
  </Toggle>
);

const SystemDropdown = ({ systems, systemOptionsMapper, selected, className, onSelection }) => {
  const [open, toggleOpen] = useToggle(false);

  return (
    <StyledDropdown isOpen={open} toggle={toggleOpen} className={className}>
      <DropdownToggle caret>{systemOptionsMapper[selected] || selected}</DropdownToggle>
      <DropdownMenu>
        {
          systems.map((system) => (
            <DropdownItem 
              key={system} 
              data-test={`${system}-view`} 
              onClick={() => onSelection(system)}
            >
              {systemOptionsMapper[system]}
            </DropdownItem>
          ))
        }
      </DropdownMenu>
    </StyledDropdown>
  );
};

const SystemDrawer = ({ systems, systemOptionsMapper, selected, className, onSelection }) => {
  const [open, toggleOpen] = useToggle(false);

  const handleOnSelection = (val) => {
    onSelection(val);
    toggleOpen();
  };

  return (
    <SystemDrawerWrapper className={className}>
      <button onClick={toggleOpen}>{systemOptionsMapper[selected] || selected}</button>
      <Collapse isOpen={open} navbar>
        {
          systems.map((system, index) => (
            <Fragment key={system}>
              {(index > 0) && <hr />}
              <button 
                data-test={`${system}-view`} 
                onClick={() => handleOnSelection(system)}
              >
                {systemOptionsMapper[system]}
              </button>
            </Fragment>
          ))
        }
      </Collapse>
    </SystemDrawerWrapper>
  );
};

const SystemPicker = ({ history, systems, systemOptionsMapper, currentSystem }) => {
  const SELECTED_SYSTEM_PLACEHOLDER = 'TIME ENTRIES';
  const { width } = useViewportSize();
  const [selected, setSelected] = useState((isArrayWithLength(systems) && systems.find((type) => type === currentSystem)) || SELECTED_SYSTEM_PLACEHOLDER);    

  useEffect(() => {
    if (isArrayWithLength(systems)) {
      setSelected(systems.find((type) => type === currentSystem) || SELECTED_SYSTEM_PLACEHOLDER);
    }
  }, [systems, currentSystem]);

  const onSelection = (selection) => {
    history.push(Routes[selection]);
  };

  return (
    <Fragment>
      { width > parseInt(theme.breakpoints.sm) ? (
        <SystemDropdown 
          systems={systems}
          systemOptionsMapper={systemOptionsMapper}
          selected={selected} 
          onSelection={onSelection} 
        /> 
      ) : (
        <SystemDrawer
          systems={systems} 
          selected={selected}
          systemOptionsMapper={systemOptionsMapper} 
          onSelection={onSelection} 
        />
      )}
    </Fragment>
  );
};

const UserMenu = ({ onLogout, className, history }) => {
  const [open, toggleOpen] = useToggle(false);

  const desktopUserActions = (
    <StyledUserDropdown 
      isOpen={open} 
      toggle={toggleOpen} 
      className={className}
    >
      <DropdownToggle data-test="sign-out-icon" caret>
        <FontAwesomeIcon icon='user-alt' />
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem onClick={() => history.push(Routes.PREFERENCES)}>Preferences</DropdownItem>
        <DropdownItem onClick={() => onLogout()}>Sign Out</DropdownItem>
      </DropdownMenu>
    </StyledUserDropdown>
  );

  return (
    <Fragment>
      {desktopUserActions}
    </Fragment>
  );
};

const Navbar = ({ history, isAuthenticated, onLogout, systems }) => {
  const dispatch = useDispatch();

  const currentSystem = useSelector(state => projectEntrySelectors.selectCurrentSystem(state));
  const isBulkEntryValid = useSelector(state => projectEntrySelectors.isCurrentSystemBulkEntryEnabled(state));
  const showBulkEntryModal = useSelector(state => state.bulkEntry.showBulkEntryModal);

  const [active, toggleActive] = useToggle(false);

  const numDirectReports = useSelector(state => state.user.getDirectReports?.totalCount);
  const systemOptionsMapper = useMemo(() => systems.reduce((acc, sys) => ({
    ...acc,
    [sys]: sys === SupportedSystems.VPIN ? 'ENGINEERING'
      : sys === SupportedSystems.GPRS ? 'PROTOTYPE REQUESTS'
        : sys === SupportedSystems.LRS ? 'LAB REQUESTS'
          : sys
  }), {}), [systems]);
  const navigate = route => _e => history.push(route);

  const handleBulkEntryClick = () => {
    if (isBulkEntryValid) {
      // Old bulk entry obj
      dispatch(projectEntryActions.setShowBulkEntryModal(true));
      // new bulk entry obj
      dispatch(bulkEntryActions.setShowBulkEntryModal(true));
    }
  }
  
  return (
    <StyledNavbar>
      <StyledLogo>
        <img src={logo} width='124' height='67' alt='logo' />
      </StyledLogo>
      {
        isAuthenticated && (
          <Fragment>
            <Hamburger active={active} onClick={toggleActive} />
            <Nav active={active}>
              <List>
                <NavItem>
                  {
                    systems && systems.length > 1
                      ? <SystemPicker systems={systems} systemOptionsMapper={systemOptionsMapper} currentSystem={currentSystem} history={history} />
                      : <NavLink onClick={navigate(Routes[systems[0]])}>{systemOptionsMapper[systems[0]]}</NavLink>
                  }
                </NavItem>
              </List>
              <List>
                {
                  numDirectReports > 0 ? 
                    <NavItem>
                      <NavLink onClick={navigate(Routes.DIRECTREPORTS)}>Direct Reports</NavLink>
                    </NavItem> : null
                } 
                <NavItem>
                  <NavLink>
                    <PlainAnchor href={powerBiReportsUrl} target="_blank" rel="noreferrer">Reports</PlainAnchor>
                  </NavLink>
                </NavItem>
                <NavItem>
                  <BulkEntryIconWrapper
                    isBulkEntryValid={isBulkEntryValid}
                    role="button" 
                    data-test="bulk-icon" 
                    onClick={handleBulkEntryClick}
                  >
                    <FontAwesomeIcon icon='calendar-alt' />
                    <BulkEntryIconLabel>Bulk Entry</BulkEntryIconLabel>
                  </BulkEntryIconWrapper>
                </NavItem>
                <NavItem>
                  <UserMenu onLogout={onLogout} history={history} />
                </NavItem>
              </List>
            </Nav>
            {showBulkEntryModal && <BulkTimeEntry />}
            <Overlay active={active} onClick={toggleActive}/>
          </Fragment>
        )
      }
    </StyledNavbar>
  );
};

export default withRouter(Navbar);
